import { useCallback, useEffect, useState } from 'react';
import {
  loginCustomer,
  signupCustomer,
  refreshCustomerToken,
  fetchCustomerProfile,
  logoutCustomer,
} from '../api/customer';
import {
  getLocalItem,
  getSessionItem,
  removeLocalItem,
  removeSessionItem,
  setLocalItem,
  setSessionItem,
} from '../utils/safeStorage';

const ACCESS_KEY = 'customerAccessToken';
const SESSION_HINT_KEY = 'customerSessionHint';
const AUTH_EVENT = 'customer-auth-updated';

export default function useCustomerAuth() {
  const [accessToken, setAccessToken] = useState(() => getSessionItem(ACCESS_KEY) || getLocalItem(ACCESS_KEY) || '');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const applyToken = async (token) => {
        setSessionItem(ACCESS_KEY, token);
        setLocalItem(ACCESS_KEY, token);
        setSessionItem(SESSION_HINT_KEY, '1');
        setLocalItem(SESSION_HINT_KEY, '1');
        setAccessToken(token);
        const me = await fetchCustomerProfile(token);
        setCustomer(me);
      };
      if (accessToken) {
        try {
          const me = await fetchCustomerProfile(accessToken);
          setCustomer(me);
          return;
        } catch (err) {
          const refreshed = await refreshCustomerToken().catch(() => null);
          if (refreshed?.accessToken) {
            await applyToken(refreshed.accessToken);
            return;
          }
          throw err;
        }
      }
      const hasSessionHint = Boolean(getSessionItem(SESSION_HINT_KEY) || getLocalItem(SESSION_HINT_KEY));
      if (!hasSessionHint) {
        setCustomer(null);
        return;
      }
      const refreshed = await refreshCustomerToken();
      if (refreshed?.accessToken) {
        await applyToken(refreshed.accessToken);
      }
    } catch (err) {
      removeSessionItem(ACCESS_KEY);
      removeLocalItem(ACCESS_KEY);
      removeSessionItem(SESSION_HINT_KEY);
      removeLocalItem(SESSION_HINT_KEY);
      setAccessToken('');
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    const handleUpdated = (event) => {
      const nextToken = event?.detail?.accessToken || getSessionItem(ACCESS_KEY) || getLocalItem(ACCESS_KEY) || '';
      const nextCustomer = event?.detail?.customer;
      if (!nextToken) {
        setAccessToken('');
        setCustomer(null);
        return;
      }
      setAccessToken(nextToken);
      if (nextCustomer) {
        setCustomer(nextCustomer);
      } else {
        fetchCustomerProfile(nextToken)
          .then(setCustomer)
          .catch(() => setCustomer(null));
      }
      setLoading(false);
    };
    window.addEventListener(AUTH_EVENT, handleUpdated);
    return () => window.removeEventListener(AUTH_EVENT, handleUpdated);
  }, []);

  const applyAuth = async (data) => {
    if (!data?.accessToken) return null;
    setSessionItem(ACCESS_KEY, data.accessToken);
    setLocalItem(ACCESS_KEY, data.accessToken);
    setSessionItem(SESSION_HINT_KEY, '1');
    setLocalItem(SESSION_HINT_KEY, '1');
    setAccessToken(data.accessToken);
    if (data.customer) {
      setCustomer(data.customer);
      window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: { accessToken: data.accessToken, customer: data.customer } }));
      return data.customer;
    }
    try {
      const me = await fetchCustomerProfile(data.accessToken);
      setCustomer(me);
      window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: { accessToken: data.accessToken, customer: me } }));
      return me;
    } catch (_) {
      window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: { accessToken: data.accessToken } }));
      return null;
    }
  };

  const login = async (payload) => {
    const data = await loginCustomer(payload);
    await applyAuth(data);
    return data.customer;
  };

  const signup = async (payload) => {
    const data = await signupCustomer(payload);
    await applyAuth(data);
    return data.customer;
  };

  const logout = async () => {
    if (accessToken) {
      try {
        await logoutCustomer(accessToken);
      } catch {
        // ignore
      }
    }
    removeSessionItem(ACCESS_KEY);
    removeLocalItem(ACCESS_KEY);
    removeSessionItem(SESSION_HINT_KEY);
    removeLocalItem(SESSION_HINT_KEY);
    setAccessToken('');
    setCustomer(null);
    window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: { accessToken: '', customer: null } }));
  };

  return { accessToken, customer, loading, login, signup, logout, refresh: bootstrap, setCustomer, applyAuth };
}
